import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ProgressSpinner } from "primereact/progressspinner";
import { RootState } from "app/store";
import { CONTACT_PROFILE_LOOKUP } from "app/redux";
import {
  getObjectFromSessionStore,
  getSessionStore,
  setObjectInSessionStore,
  setSessionStore,
} from "app/utils/sessionStore";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { useIPContext } from "app/pages/InteractiveProposal/InteractiveProposalProvider";
import PageHeader from "app/pages/InteractiveProposal/InteractiveProposalHeader";
import InteractiveReviewCenterSidebar from "./InteractiveReviewCenterSidebar";
import { makeRequest } from "app/utils/makeRequest";
import { ProposalTypes, THEME, USERTYPE, planHasCreditType } from "app/utils/types";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import { IRCContext, IRCLockState } from "./IRCContext";
import IRCPageGuard from "./IRCPageGuard";

const InteractiveReviewCenterLayout = () => {
  const lookUpResponse = useSelector((state: RootState) => state?.contactProfileLookup);
  const { isLoading } = lookUpResponse;
  const dispatch = useDispatch();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCommentMode, setIsCommentMode] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [selectedYear, setSelectedYear] = useState<number>(1);
  const [animationData, setAnimationData] = useState<any>(null);
  const lottieRef = useRef<any>(null);
  const [selectedCreditType, setSelectedCreditType] = useState<string>("preCredit");

  const {
    summaryData: proposalSummary,
    summaryLoading,
    themeState,
    setThemeState,
  } = useIPContext();

  // The IRC route is mounted outside RootLayout's ThemeContext.Provider, so we
  // provide one here wired to the InteractiveProposalProvider's themeState (the
  // value the header toggles). This makes useContext(ThemeContext) reactive for
  // every IRC component.
  const themeContextValue = useMemo(
    () => ({
      isDarkMode: themeState === THEME.DARK,
      setIsDarkMode: (val: boolean | ((prev: boolean) => boolean)) => {
        const next = typeof val === "function" ? val(themeState === THEME.DARK) : val;
        setThemeState(next ? THEME.DARK : THEME.LIGHT);
      },
    }),
    [themeState, setThemeState],
  );
  const getlookUpResponse = getObjectFromSessionStore("lookUpResponse");
  const location = useLocation();
  const pathName = location.pathname.split("/")[2];
  const planId = getSessionStore("planInfo")?.planId;
  const [summaryData, setSummaryData] = useState<any | null>(null);
  const [, setLoading] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  // User type — only Contact can pay fees
  const userAccess = getObjectFromLocalStore("useraccess");
  const isContact = userAccess?.userType === USERTYPE.CONTACT;

  const navigate = useNavigate();

  const headerTitle = summaryData?.entity?.title ?? proposalSummary?.employerName;
  const headerSubtitle = summaryData?.contacts
    ? `${summaryData?.contacts?.profile?.firstName ?? ""} ${
        summaryData?.contacts?.profile?.lastName ?? ""
      }`.trim()
    : `${proposalSummary?.primaryContact?.firstName ?? ""} ${
        proposalSummary?.primaryContact?.lastName ?? ""
      }`.trim();
  const headerProposalNo = summaryData?.proposalNo ?? proposalSummary?.proposalNo;
  const headerProposalType = proposalSummary?.proposalType ?? summaryData?.planType;

  // ── Lock state for the currently selected year ──────────────────────────────
  const lockState = useMemo<IRCLockState>(() => {
    const reviews: any[] = summaryData?.planReviews ?? [];
    const review = reviews.find((r: any) => r.planYear === selectedYear);
    if (!review || !review.reviewOpenDate) return "pre_window";
    const storedDate = getSessionStore("currentDate");
    const now = storedDate === true ? new Date(review.reviewOpenDate).getTime() + 1 : Date.now();
    const invoices: any[] = review.planFeeInvoice ?? [];
    if (now < new Date(review.reviewOpenDate).getTime()) return "pre_window";
    if (review.anniversaryDate && now > new Date(review.anniversaryDate).getTime())
      // eslint-disable-next-line
      return "pre_window";
    const feeLocked = invoices.length > 0 && invoices.every((inv: any) => inv.status === false);
    if (feeLocked) return "fee_locked";
    return "unlocked";
  }, [summaryData, selectedYear]);

  const reviewOpenDate = useMemo<string | null>(() => {
    const reviews: any[] = summaryData?.planReviews ?? [];
    const review = reviews.find((r: any) => r.planYear === selectedYear);
    return review?.reviewOpenDate ?? null;
  }, [summaryData, selectedYear]);

  const calendarYear = useMemo<number>(() => {
    const reviews: any[] = summaryData?.planReviews ?? [];
    const review = reviews.find((r: any) => r.planYear === selectedYear);
    return review?.year ?? new Date().getFullYear();
  }, [summaryData, selectedYear]);

  const refreshSummary = useCallback(() => setRefreshCount((c) => c + 1), []);
  const onPayNow = useCallback(() => navigate("/interactive-review-center/funding"), [navigate]);
  // ────────────────────────────────────────────────────────────────────────────

  const animationURL = (type?: string) => {
    if (type === ProposalTypes.KeyPersonInsurance) {
      return "https://iapp.alexmelik.com/wp-content/uploads/2024/08/Key-Person.json";
    }
    if (type === ProposalTypes.BuySell) {
      return "https://iapp.alexmelik.com/wp-content/uploads/2023/09/Buy-Sell.json";
    }
    if (type === ProposalTypes.PremiumFinance) {
      return "https://iapp.alexmelik.com/wp-content/uploads/2023/09/Premium-Finance.json";
    }
    return "https://iapp.alexmelik.com/wp-content/uploads/2023/09/Split-Dollar.json";
  };

  useEffect(() => {
    if (!headerProposalType) return;
    fetch(animationURL(headerProposalType))
      .then((res) => res.json())
      .then((data) => setAnimationData(data));
  }, [headerProposalType]);

  // On mount: always reset to defaults and seed sessionStorage
  useEffect(() => {
    setSessionStore("ircSelectedYear", 1);
    setSessionStore("ircSelectedCreditType", "preCredit");
    // eslint-disable-next-line
  }, []);

  // Keep sessionStorage in sync whenever user changes year/creditType
  useEffect(() => {
    setSessionStore("ircSelectedYear", selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    setSessionStore("ircSelectedCreditType", selectedCreditType);
  }, [selectedCreditType]);

  // Scroll to top on route change
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [pathName]);

  const formatCreditTypeLabel = (ct: string) =>
    ct === "postCredit" ? "Post-Credit" : "Pre-Credit";

  function mapReviewsToSteps(
    reviews: any[],
    selectedYearValue: number,
    selectedCreditTypeValue: string,
    // Pre-Credit/Post-Credit applies to Premium Finance and Split Dollar — Buy Sell
    // and Key Person just show review status.
    showCreditLabel = false,
  ): any[] {
    return reviews.map((r) => {
      // No review window configured → locked
      if (!r.reviewOpenDate) {
        return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED", completed: false };
      }

      const storedDate = getSessionStore("currentDate");
      const now = storedDate === true ? new Date(r.reviewOpenDate).getTime() + 1 : Date.now();

      // Outside review window → locked
      if (
        now < new Date(r.reviewOpenDate).getTime() ||
        (r.anniversaryDate && now > new Date(r.anniversaryDate).getTime())
      ) {
        return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED", completed: false };
      }

      // Window is open — derive status from review counts (null-safe)
      const confirmed = r.confirmedBy ?? 0;
      const skipped = r.skippedBy ?? 0;
      const total = r.total ?? 0;

      if (total > 0 && confirmed === total) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "COMPLETED",
          label: "Confirm (All)",
          completed: true,
        };
      }
      if (total > 0 && skipped === total) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "SKIPPED",
          label: "Skip (All)",
          completed: false,
        };
      }
      if (confirmed > 0) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "COMPLETED",
          label: `Confirm (${confirmed}))`,
          completed: false,
        };
      }
      if (skipped > 0) {
        return {
          yearNumber: r.planYear,
          calendarYear: r.year,
          status: "SKIPPED",
          label: `Skip (${skipped})`,
          completed: false,
        };
      }

      // Open window, nothing reviewed → CURRENT (clock); credit label only on selected year
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "CURRENT",
        completed: false,
        label:
          showCreditLabel && r.planYear === selectedYearValue
            ? formatCreditTypeLabel(selectedCreditTypeValue)
            : undefined,
      };
    });
  }

  // Ensure lookup data is available for header branding
  useEffect(() => {
    if (!getlookUpResponse?.menu) {
      dispatch({
        type: CONTACT_PROFILE_LOOKUP,
        payload: { address: "lookupAfter" },
      });
    }
  }, []);

  useEffect(() => {
    if (lookUpResponse?.data?.menu) {
      setObjectInSessionStore("lookUpResponse", lookUpResponse?.data);
    }
  }, [lookUpResponse]);

  useEffect(() => {
    const handler = (e: Event) => {
      const { detail } = e as CustomEvent;
      if (detail?.year != null) {
        setSelectedYear(detail.year);
        setSessionStore("ircSelectedYear", detail.year);
      }
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;

    setLoading(true);
    // creditType (pre/post) only applies to Premium Finance / Split Dollar.
    const creditQuery = planHasCreditType(getSessionStore("planInfo")?.planType)
      ? `&creditType=${selectedCreditType}`
      : "";
    makeRequest("interactiveReviewCenterSummary")
      .get(`${planId}?year=${selectedYear}${creditQuery}`, true)
      .then((res: any) => {
        if (cancelled) return;
        const data: any = res?.data?.data;
        setSummaryData(data);
      })
      .catch((err: any) => {
        if (!cancelled) console.error("Failed to fetch review center summary:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // eslint-disable-next-line
    return () => {
      cancelled = true;
    };
  }, [planId, selectedYear, selectedCreditType, refreshCount]);

  const yearSteps = useMemo(
    () =>
      summaryData
        ? mapReviewsToSteps(
            summaryData.planReviews,
            selectedYear,
            selectedCreditType,
            planHasCreditType(getSessionStore("planInfo")?.planType),
          )
        : [],
    [summaryData, selectedYear, selectedCreditType],
  );

  const ircContextValue = useMemo(
    () => ({
      lockState,
      reviewOpenDate,
      calendarYear,
      isContact,
      onPayNow,
      refreshSummary,
      yearSteps,
      selectedYear,
      selectedCreditType,
    }),
    [
      lockState,
      reviewOpenDate,
      calendarYear,
      isContact,
      onPayNow,
      refreshSummary,
      yearSteps,
      selectedYear,
      selectedCreditType,
    ],
  );

  const headerLoadingPage = isLoading;

  const loadingPage = isLoading || summaryLoading;

  const mainContent = () => {
    if (loadingPage) {
      return (
        <div className="flex justify-center items-center h-full">
          <ProgressSpinner />
        </div>
      );
    }
    return (
      <IRCPageGuard>
        <Outlet />
      </IRCPageGuard>
    );
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <IRCContext.Provider value={ircContextValue}>
        <div className={`flex h-screen ${isCommentMode ? "comment-mode" : ""}`}>
          <PageHeader
            title={headerTitle}
            subtitle={headerSubtitle}
            proposalNo={headerProposalNo}
            proposalType={headerProposalType}
            error={null}
            loadingPage={headerLoadingPage}
            isCollapsed={isCollapsed}
            animationData={animationData}
            setIsHovered={() => {}}
            lottieRef={lottieRef}
            setIsCommentMode={setIsCommentMode}
            isCommentMode={isCommentMode}
            yearData={yearSteps}
            creditTypeData={summaryData?.selectedCreditType ?? selectedCreditType}
            selectedYearNumber={selectedYear}
            allowLockedYearClick
            onPhaseClick={(phase) => {
              setSelectedCreditType(phase);
              setSessionStore("ircSelectedCreditType", phase);
              window.dispatchEvent(
                new CustomEvent("ircSelectionChanged", { detail: { creditType: phase } }),
              );
            }}
          />

          <InteractiveReviewCenterSidebar
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
          />
          <div
            ref={mainContentRef}
            className="flex-1 overflow-auto overflow-x-hidden h-[calc(100vh-70px)] mt-auto py-4 pl-6 pr-4 scrollbar-hide"
          >
            {mainContent()}
          </div>
        </div>
      </IRCContext.Provider>
    </ThemeContext.Provider>
  );
};

export default InteractiveReviewCenterLayout;
